import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Description as DescriptionIcon,
    PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import ValuationHeaderCard from './ValuationHeaderCard';
import ValuationSummaryCard from './ValuationSummaryCard';
import ValuationPdfPreviewSection from './ValuationPdfPreviewSection';
import ValuationOrdersSection from './ValuationOrdersSection';
import ValuationAdjustmentsSection from './ValuationAdjustmentsSection';
import ValuationHistorySection from './ValuationHistorySection';
import ValuationStateActions from './ValuationStateActions';
import { useValuations } from '../../../hooks/administration/useValuations';
import { useAuthContext } from '../../../context/AuthProvider';

const TabPanel = ({ value, index, children }) => {
    if (value !== index) return null;
    return <Box sx={{ pt: 2 }}>{children}</Box>;
};

const ValuationDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const valuationId = Number(id);

    const { user } = useAuthContext();

    const adminID = Number(process.env.REACT_APP_ROL_ADMIN_ID);
    const administradorID = Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID);
    const gerenciaID = Number(process.env.REACT_APP_ROL_GERENTE_ID);

    const userRoleID = Number(user?.RolID || user?.rolid || user?.RolId);

    const isAdmin = userRoleID === adminID;
    const isAdministrador = userRoleID === administradorID;
    const isGerencia = userRoleID === gerenciaID;

    const canViewHistory = isAdmin || isGerencia;

    const [tab, setTab] = useState(0);
    const [openSummaryDialog, setOpenSummaryDialog] = useState(false);
    const [openPdfDialog, setOpenPdfDialog] = useState(false);

    const {
        useFetchValuation,
        useValuationSummary,
        useValuationPdfData,
    } = useValuations();

    const {
        data: valuation,
        isLoading: loadingValuation,
        error: valuationError,
    } = useFetchValuation(valuationId);

    const {
        data: summary,
        isLoading: loadingSummary,
        error: summaryError,
    } = useValuationSummary(valuationId);

    const {
        data: pdfData,
        isLoading: loadingPdfData,
        error: pdfError,
    } = useValuationPdfData(valuationId);

    if (loadingValuation || loadingSummary) {
        return (
            <Box
                minHeight="320px"
                display="flex"
                justifyContent="center"
                alignItems="center"
            >
                <CircularProgress size={56} />
            </Box>
        );
    }

    if (valuationError || summaryError) {
        return (
            <Alert severity="error" sx={{ my: 3 }}>
                {valuationError?.message ||
                    summaryError?.message ||
                    'Error al cargar el detalle de la valorización.'}
            </Alert>
        );
    }

    if (!valuation) {
        return (
            <Alert severity="warning" sx={{ my: 3 }}>
                No se encontró la valorización solicitada.
            </Alert>
        );
    }

    /*
    const orders = summary?.orders || [];
    const adjustments = summary?.adjustments || [];
    const editable = ['CREADA', 'EN_PROCESO'].includes(valuation?.valcabest);
    */

    const details = valuation?.details || [];
    const adjustments = summary?.adjustments || [];
    const editable = ['CREADA', 'EN_PROCESO'].includes(valuation?.valcabest);

    return (
        <Box>
            {/* navegación */}
            <Paper
                sx={{
                    p: 2.5,
                    borderRadius: 3,
                    mb: 3,
                    backgroundColor: '#fafafa',
                }}
            >
                <Button
                    variant="text"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/administracion/valorizaciones')}
                    sx={{ textTransform: 'none', pl: 0 }}
                >
                    Volver al listado
                </Button>
            </Paper>

            <Box display="flex" flexDirection="column" gap={2.5}>
                {/* cabecera */}
                <ValuationHeaderCard valuation={valuation} />

                {/* resumen económico */}
                <ValuationSummaryCard valuation={valuation} />

                {/* tabs */}
                <Paper sx={{ borderRadius: 3 }}>
                    <Tabs
                        value={tab}
                        onChange={(_, value) => setTab(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label={`Ítems (${details.length})`} />
                        <Tab label={`Ajustes (${adjustments.length})`} />
                        {canViewHistory && <Tab label="Historial" />}

                        {/*<Tab label="Vista PDF" />*/}
                    </Tabs>

                    <Divider />

                    <TabPanel value={tab} index={0}>
                        <Box p={2}>
                            <ValuationOrdersSection
                                valuation={valuation}
                                editable={editable}
                            />
                        </Box>
                    </TabPanel>

                    <TabPanel value={tab} index={1}>
                        <Box p={2}>
                            <ValuationAdjustmentsSection
                                valuation={valuation}
                                adjustments={adjustments}
                                editable={editable}
                            />
                        </Box>
                    </TabPanel>

                    {canViewHistory &&
                        <TabPanel value={tab} index={2}>
                            <Box p={2}>
                                <ValuationHistorySection valuationId={valuationId} />
                            </Box>
                        </TabPanel>
                    }

                    {/*
                    <TabPanel value={tab} index={3}>
                        <Box p={2}>
                            {loadingPdfData ? (
                                <Box
                                    minHeight="220px"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <CircularProgress />
                                </Box>
                            ) : pdfError ? (
                                <Alert severity="error">
                                    {pdfError?.message || 'Error al cargar los datos para PDF.'}
                                </Alert>
                            ) : (
                                <ValuationPdfPreviewSection data={pdfData} />
                            )}
                        </Box>
                    </TabPanel>
                    */}
                </Paper>

                {/* acciones y estado */}
                <ValuationStateActions
                    valuation={valuation}
                    onOpenSummary={() => setOpenSummaryDialog(true)}
                    onOpenPdfData={() => setOpenPdfDialog(true)}
                />
            </Box>

            {/* Dialog resumen */}
            <Dialog
                open={openSummaryDialog}
                onClose={() => setOpenSummaryDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <DescriptionIcon color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            Resumen de valorización
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ backgroundColor: '#fafafa' }}>
                    <ValuationSummaryCard valuation={valuation} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSummaryDialog(false)}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog datos PDF */}
            <Dialog
                open={openPdfDialog}
                onClose={() => setOpenPdfDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <PictureAsPdfIcon color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            Datos preparados para PDF
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ backgroundColor: '#fafafa' }}>
                    {loadingPdfData ? (
                        <Box
                            minHeight="220px"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <CircularProgress />
                        </Box>
                    ) : pdfError ? (
                        <Alert severity="error">
                            {pdfError?.message || 'Error al cargar los datos para PDF.'}
                        </Alert>
                    ) : (
                        <ValuationPdfPreviewSection data={pdfData} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPdfDialog(false)}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ValuationDetail;